import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    Paper,
    Button,
    Skeleton,
    Snackbar,
    Alert,
    CircularProgress,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import cookie from "react-cookies";
import { subdomain, getBackendUrl } from "../../auth/utilities";

export default function OnboardVendorAssessment() {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, type: "success", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [completed, setCompleted] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");
    const allAnswered = questions.length > 0 && questions.every((q) => !!q.response);
    const backend = getBackendUrl();
    const csrfToken = cookie.load("csrftoken");

    // Fetch questions on load
    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                const res = await fetch(
                    `${backend}${subdomain}/en/tprm/vendor-onboard-assessment-questionnaire-view/?token=${token}`,
                    {
                        method: "GET",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                    }
                );
                if (res.status === 403) {
                    // Session expired or invalid — redirect to login
                    navigate(`/vendor-assessment/?token=${token}`, { replace: true });
                    return;
                }
                if (!res.ok) throw new Error("Token expired or invalid");
                const data = await res.json();
                if (data?.questions) {
                    setQuestions(data.questions);
                } else {
                    throw new Error("No questions found");
                }
            } catch (error) {
                 setFetchError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchQuestions();
    }, [token, backend, navigate]);

    // Handle change + auto save
    const handleResponseChange = async (id, value) => {
        setQuestions((prev) =>
            prev.map((q) =>
                q.vendor_onboarding_answer_id === id ? { ...q, response: value } : q
            )
        );

        try {
            const res = await fetch(
                `${backend}${subdomain}/en/tprm/update-vendor-onboard-answer/?token=${token}`,
                {
                    method: "PUT",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify({
                        vendor_onboarding_answer_id: id,
                        vendor_questionnaire_choice: value,
                    }),
                }
            );

            if (res.status === 403) {
                navigate(`/vendor-assessment/?token=${token}`, { replace: true });
                return;
            }

            setSnackbar({
                open: true,
                type: "success",
                message: "Answer updated successfully ✅",
            });
        } catch (error) {
            setSnackbar({
                open: true,
                type: "error",
                message: "Failed to update answer ❌",
            });
        }
    };

    // Submit All
    const handleSubmitAll = async () => {
        setSubmitting(true);
        try {
            const res = await fetch(
                `${backend}${subdomain}/en/tprm/vendor-onboard-assessment-submit/?token=${token}`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify({
                        assessment_id: questions?.[0]?.assessment_id,
                    }),
                }
            );

            if (res.status === 403) {
                navigate(`/vendor-assessment/?token=${token}`, { replace: true });
                return;
            }

            if (!res.ok) throw new Error("Submit failed");

            setCompleted(true);
        } catch (error) {
            setSnackbar({
                open: true,
                type: "error",
                message: "Failed to submit assessment ❌",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // Skeleton loading
    if (loading) {
        return (
            <Box p={3}>
                {[...Array(5)].map((_, i) => (
                    <Paper
                        key={i}
                        variant="outlined"
                        sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: "10px",
                            border: "1px solid #ddd",
                        }}
                    >
                        <Skeleton variant="text" width="60%" height={30} />
                        <Skeleton variant="rectangular" width="100%" height={40} sx={{ mt: 2 }} />
                    </Paper>
                ))}
            </Box>
        );
    }

    // Token expired
    if (fetchError) {
        return (
            <Box
                height="100vh"
                display="flex"
                justifyContent="center"
                alignItems="center"
                bgcolor="#f8f9fb"
            >
                <Paper
                    sx={{
                        p: 4,
                        borderRadius: "12px",
                        textAlign: "center",
                        maxWidth: "400px",
                    }}
                    elevation={3}
                >
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Token Expired ⚠️
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Your session has ended. Please request a new link from your administrator to
                        continue the vendor assessment.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // Completed screen
    if (completed) {
        return (
            <Box
                height="100vh"
                display="flex"
                justifyContent="center"
                alignItems="center"
                bgcolor="#f8f9fb"
            >
                <Paper
                    sx={{
                        p: 4,
                        borderRadius: "12px",
                        textAlign: "center",
                        maxWidth: "400px",
                    }}
                    elevation={3}
                >
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="success.main">
                        🎉 Assessment Completed
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        You have successfully completed the vendor assessment.
                        Thank you for your responses!
                    </Typography>
                </Paper>
            </Box>
        );
    }

    const answeredCount = questions.filter((q) => !!q.response).length;
    const progress = Math.round((answeredCount / questions.length) * 100);

    return (
        <Box display="flex" flexDirection="column" height="100vh" bgcolor="#f8f9fb">
            {/* Header */}
            <Box p={3} borderBottom="1px solid #ddd" bgcolor="white" display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" fontWeight="bold">
                    Third-Party Risk Management Assessment
                </Typography>
                {/* Progress Circle */}
                <Box display="flex" alignItems="center">
                    <CircularProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            color: "#001B2B",
                            mr: 1,
                        }}
                        size={40}
                        thickness={5}
                    />
                    <Typography variant="body2" fontWeight="bold">{progress}%</Typography>
                </Box>
            </Box>

            {/* Questions List */}
            <Box flex={1} p={3} overflow="auto">
                {questions.map((q, index) => (
                    <Paper
                        key={q.vendor_onboarding_answer_id}
                        variant="outlined"
                        sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: "10px",
                            border: "1px solid #ddd",
                        }}
                    >
                        {/* Question text */}
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {index + 1}. {q.question_description}
                        </Typography>

                        {/* Options */}
                        <RadioGroup
                            row
                            value={q.response || ""}
                            onChange={(e) =>
                                handleResponseChange(q.vendor_onboarding_answer_id, e.target.value)
                            }
                        >
                            <FormControlLabel
                                value="YES"
                                control={<Radio sx={{ "&.Mui-checked": { color: "#001B2B" } }} />}
                                label="Yes"
                            />
                            <FormControlLabel
                                value="NO"
                                control={<Radio sx={{ "&.Mui-checked": { color: "#001B2B" } }} />}
                                label="No"
                            />
                            <FormControlLabel
                                value="PARTIAL"
                                control={<Radio sx={{ "&.Mui-checked": { color: "#001B2B" } }} />}
                                label="Partial"
                            />
                        </RadioGroup>
                    </Paper>
                ))}
            </Box>

            {/* Footer with Submit All */}
            <Box
                p={2}
                borderTop="1px solid #ddd"
                display="flex"
                justifyContent="flex-end"
                bgcolor="white"
            >
                <Button
                    variant="contained"
                    onClick={handleSubmitAll}
                    sx={{
                        backgroundColor: "#001B2B",
                        "&:hover": { backgroundColor: "#00324D" },
                        textTransform: "none",
                    }}
                    disabled={submitting || !allAnswered}
                >
                    {submitting ? "Submitting..." : "Submit All"}
                </Button>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.type} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
